/**
 * Study Planner Service
 * 
 * A completely separate module from Interview functionality.
 * Handles WebSocket communication for study planning, roadmap generation,
 * practice questions, and resource discovery.
 * 
 * Storage Strategy:
 * - candidateGoal (String): Stores the goal type (e.g., "get_job", "career_switch")
 * - candidateData (AWSJSON): Stores the complete study planner data including:
 *   - goalDetails: Full goal configuration
 *   - roadmap: Generated study roadmap
 *   - progress: Topic completion status
 *   - sessionId: Active study session ID
 */

// Goal type constants
export const GOAL_TYPES = {
  GET_JOB: 'get_job',
  SKILL_IMPROVEMENT: 'skill_improvement',
  CAREER_SWITCH: 'career_switch',
  INTERVIEW_PREP: 'interview_prep',
  CERTIFICATION: 'certification',
  PROMOTION: 'promotion'
};

// Goal type display information
export const GOAL_TYPE_INFO = {
  [GOAL_TYPES.GET_JOB]: {
    label: 'Land a Job',
    description: 'Prepare for a specific job role',
    color: '#4CAF50'
  },
  [GOAL_TYPES.SKILL_IMPROVEMENT]: {
    label: 'Improve Skills',
    description: 'Level up specific technical skills',
    color: '#2196F3'
  },
  [GOAL_TYPES.CAREER_SWITCH]: {
    label: 'Switch Careers',
    description: 'Transition to a new career path',
    color: '#9C27B0'
  },
  [GOAL_TYPES.INTERVIEW_PREP]: {
    label: 'Interview Prep',
    description: 'General interview preparation',
    color: '#FF9800'
  },
  [GOAL_TYPES.CERTIFICATION]: {
    label: 'Get Certified',
    description: 'Prepare for certification exams',
    color: '#F44336'
  },
  [GOAL_TYPES.PROMOTION]: {
    label: 'Get Promoted',
    description: 'Prepare for career advancement',
    color: '#00BCD4'
  }
};

// Resource type constants
export const RESOURCE_TYPES = {
  ALL: 'all',
  VIDEO: 'video',
  ARTICLE: 'article',
  COURSE: 'course',
  PRACTICE: 'practice'
};

// Topic status constants
export const TOPIC_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
};

// WebSocket message types
export const WS_MESSAGE_TYPES = {
  // Outgoing messages
  START_STUDY_PLANNER: 'start_study_planner',
  GENERATE_ROADMAP: 'generate_roadmap',
  GET_PRACTICE_QUESTIONS: 'get_practice_questions',
  SEARCH_RESOURCES: 'search_resources',
  UPDATE_PROGRESS: 'update_progress',
  GET_ROADMAP: 'get_roadmap',
  
  // Incoming messages
  STUDY_PLANNER_STARTED: 'study_planner_started',
  ROADMAP_GENERATING: 'roadmap_generating',
  ROADMAP_GENERATED: 'roadmap_generated',
  ROADMAP_RETRIEVED: 'roadmap_retrieved',
  ROADMAP_NOT_FOUND: 'roadmap_not_found',
  PRACTICE_QUESTIONS: 'practice_questions',
  RESOURCES_FOUND: 'resources_found',
  PROGRESS_UPDATED: 'progress_updated',
  STUDY_PLANNER_ERROR: 'study_planner_error'
};

/**
 * StudyPlannerService Class
 * Manages WebSocket connection and study planner operations
 */
class StudyPlannerService {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.callbacks = {};
    this.connectionState = 'disconnected';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.messageQueue = [];
    this.serverUrl = null;
  }

  /**
   * Connect to WebSocket server
   * @param {string} serverUrl - WebSocket server URL (e.g., wss://phone.getscreened.in/getscreenedbot/ws)
   * @returns {Promise<void>}
   */
  connect(serverUrl) {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.serverUrl = serverUrl;
      this.connectionState = 'connecting';
      
      try {
        this.ws = new WebSocket(serverUrl);

        this.ws.onopen = () => {
          console.log('[StudyPlannerService] Connected to server');
          this.connectionState = 'connected';
          this.reconnectAttempts = 0;
          
          // Process queued messages
          this._processMessageQueue();
          
          this.emit('connected', { serverUrl });
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this._handleMessage(message);
          } catch (error) {
            console.error('[StudyPlannerService] Failed to parse message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[StudyPlannerService] WebSocket error:', error);
          this.emit('error', { error: 'Connection error' });
        };

        this.ws.onclose = (event) => {
          console.log('[StudyPlannerService] Connection closed:', event.code, event.reason);
          this.connectionState = 'disconnected';
          this.emit('disconnected', { code: event.code, reason: event.reason });
          
          // Attempt reconnection
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this._attemptReconnect();
          }
        };

        // Timeout for connection
        setTimeout(() => {
          if (this.connectionState === 'connecting') {
            reject(new Error('Connection timeout'));
          }
        }, 10000);

      } catch (error) {
        this.connectionState = 'disconnected';
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnected');
      this.ws = null;
    }
    this.sessionId = null;
    this.connectionState = 'disconnected';
    this.messageQueue = [];
  }

  /**
   * Attempt to reconnect to the server
   * @private
   */
  _attemptReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`[StudyPlannerService] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    setTimeout(() => {
      if (this.serverUrl && this.connectionState === 'disconnected') {
        this.connect(this.serverUrl).catch(() => {
          // Will retry if max attempts not reached
        });
      }
    }, delay);
  }

  /**
   * Handle incoming WebSocket messages
   * @private
   * @param {Object} message - Parsed message object
   */
  _handleMessage(message) {
    console.log('[StudyPlannerService] Received:', message.type, message);

    switch (message.type) {
      // Session Management
      case WS_MESSAGE_TYPES.STUDY_PLANNER_STARTED:
        this.sessionId = message.session_id;
        this.emit('sessionStarted', {
          sessionId: message.session_id,
          success: message.success,
          message: message.message,
          timestamp: message.timestamp
        });
        break;

      // Roadmap Generation
      case WS_MESSAGE_TYPES.ROADMAP_GENERATING:
        this.emit('roadmapProgress', {
          message: message.message,
          sessionId: message.session_id,
          timestamp: message.timestamp
        });
        break;

      case WS_MESSAGE_TYPES.ROADMAP_GENERATED:
        this.emit('roadmapReady', {
          success: message.success,
          roadmap: message.roadmap,
          timestamp: message.timestamp
        });
        break;

      case WS_MESSAGE_TYPES.ROADMAP_RETRIEVED:
        this.emit('roadmapLoaded', {
          success: message.success,
          roadmap: message.roadmap,
          progress: message.progress,
          timestamp: message.timestamp
        });
        break;

      case WS_MESSAGE_TYPES.ROADMAP_NOT_FOUND:
        this.emit('roadmapNotFound', {
          success: false,
          message: message.message,
          sessionId: message.session_id,
          timestamp: message.timestamp
        });
        break;

      // Practice Questions
      case WS_MESSAGE_TYPES.PRACTICE_QUESTIONS:
        this.emit('questionsReady', {
          skill: message.skill,
          questions: message.questions,
          timestamp: message.timestamp
        });
        break;

      // Resources
      case WS_MESSAGE_TYPES.RESOURCES_FOUND:
        this.emit('resourcesReady', {
          topic: message.topic,
          resourceType: message.resource_type,
          resources: message.resources,
          summary: message.summary,
          searchReasoning: message.search_reasoning,
          timestamp: message.timestamp
        });
        break;

      // Progress Updates
      case WS_MESSAGE_TYPES.PROGRESS_UPDATED:
        this.emit('progressUpdated', {
          success: message.success,
          topicId: message.topic_id,
          status: message.status,
          overallProgress: message.overall_progress,
          timestamp: message.timestamp
        });
        break;

      // Errors
      case WS_MESSAGE_TYPES.STUDY_PLANNER_ERROR:
        this.emit('studyPlannerError', {
          error: message.error,
          timestamp: message.timestamp
        });
        break;

      default:
        console.log('[StudyPlannerService] Unknown message type:', message.type);
    }
  }

  /**
   * Send a message through WebSocket
   * @private
   * @param {Object} message - Message to send
   */
  _send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message for later
      this.messageQueue.push(message);
      console.warn('[StudyPlannerService] WebSocket not connected. Message queued.');
    }
  }

  /**
   * Process queued messages
   * @private
   */
  _processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this._send(message);
    }
  }

  // ============================================
  // Event Handling
  // ============================================

  /**
   * Register an event callback
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    this.callbacks[event].push(callback);
  }

  /**
   * Remove an event callback
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit an event
   * @private
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[StudyPlannerService] Error in ${event} callback:`, error);
        }
      });
    }
  }

  // ============================================
  // PUBLIC API METHODS
  // ============================================

  /**
   * Start a new study planner session
   * @param {Object} candidateData - Candidate profile data
   * @param {Object} goalData - Goal configuration
   * @returns {string} Generated session ID
   */
  startSession(candidateData, goalData) {
    const sessionId = `study_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this._send({
      type: WS_MESSAGE_TYPES.START_STUDY_PLANNER,
      session_id: sessionId,
      candidate_data: {
        id: candidateData.id,
        name: candidateData.fullName || candidateData.name,
        email: candidateData.email,
        highlighted_skills: this._parseSkills(candidateData.keySkills),
        experience: candidateData.workStatus,
        education: candidateData.educationDetails,
        professionalSummary: candidateData.description
      },
      goal_data: {
        goal_type: goalData.goalType,
        target_role: goalData.targetRole,
        target_skills: goalData.targetSkills || [],
        timeline_weeks: goalData.timelineWeeks || 12,
        study_hours_per_day: goalData.studyHoursPerDay || 2
      }
    });

    return sessionId;
  }

  /**
   * Parse skills string into structured format
   * @private
   * @param {string} skillsString - Comma-separated skills
   * @returns {Array} Parsed skills array
   */
  _parseSkills(skillsString) {
    if (!skillsString) return [];
    
    return skillsString.split(',').map((skill, index) => ({
      skill: skill.trim(),
      weight: 5, // Default intermediate level
      years: 1   // Default experience
    })).filter(s => s.skill);
  }

  /**
   * Generate a personalized study roadmap
   * @param {Object} preferences - Generation preferences
   */
  generateRoadmap(preferences = {}) {
    if (!this.sessionId) {
      console.error('[StudyPlannerService] No active session. Call startSession first.');
      this.emit('studyPlannerError', { error: 'No active session' });
      return;
    }

    this._send({
      type: WS_MESSAGE_TYPES.GENERATE_ROADMAP,
      session_id: this.sessionId,
      preferences: {
        study_hours_per_day: preferences.studyHoursPerDay || 2,
        focus_on_practice: preferences.focusOnPractice !== false,
        include_certifications: preferences.includeCertifications || false,
        preferred_resource_types: preferences.preferredResourceTypes || ['video', 'practice']
      }
    });
  }

  /**
   * Get practice questions for a skill
   * @param {string} skill - Skill name
   * @param {number} count - Number of questions (default: 5)
   */
  getPracticeQuestions(skill, count = 5) {
    this._send({
      type: WS_MESSAGE_TYPES.GET_PRACTICE_QUESTIONS,
      skill: skill,
      count: count
    });
  }

  /**
   * Search for learning resources
   * @param {string} topic - Topic to search for
   * @param {string} resourceType - Type of resource (all, video, article, course, practice)
   */
  searchResources(topic, resourceType = RESOURCE_TYPES.ALL) {
    this._send({
      type: WS_MESSAGE_TYPES.SEARCH_RESOURCES,
      topic: topic,
      resource_type: resourceType
    });
  }

  /**
   * Update progress for a topic
   * @param {string} topicId - Topic identifier
   * @param {Object} progressData - Progress data
   */
  updateProgress(topicId, progressData) {
    if (!this.sessionId) {
      console.error('[StudyPlannerService] No active session');
      this.emit('studyPlannerError', { error: 'No active session' });
      return;
    }

    this._send({
      type: WS_MESSAGE_TYPES.UPDATE_PROGRESS,
      session_id: this.sessionId,
      topic_id: topicId,
      progress_data: {
        status: progressData.status,
        hours_spent: progressData.hoursSpent || 0,
        completion_date: progressData.completionDate || new Date().toISOString(),
        notes: progressData.notes || '',
        confidence_level: progressData.confidenceLevel || 5
      }
    });
  }

  /**
   * Retrieve an existing roadmap
   */
  getRoadmap() {
    if (!this.sessionId) {
      console.error('[StudyPlannerService] No active session');
      this.emit('studyPlannerError', { error: 'No active session' });
      return;
    }

    this._send({
      type: WS_MESSAGE_TYPES.GET_ROADMAP,
      session_id: this.sessionId
    });
  }

  /**
   * Set the session ID (for restoring sessions)
   * @param {string} sessionId - Session ID to restore
   */
  setSessionId(sessionId) {
    this.sessionId = sessionId;
  }

  /**
   * Get current session ID
   * @returns {string|null} Current session ID
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Check if connected
   * @returns {boolean} Connection status
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   * @returns {string} Connection state
   */
  getConnectionState() {
    return this.connectionState;
  }
}

// Export singleton instance
export const studyPlannerService = new StudyPlannerService();

// Export class for testing or multiple instances
export default StudyPlannerService;
